import React, { useState, useEffect } from 'react';
import LeadList from './LeadList';
import LeadForm from './LeadForm';
import LeadStats from './LeadStats';
import AdvancedLeadFilters from './AdvancedLeadFilters';
import BulkLeadAssignmentModal from './BulkLeadAssignmentModal';
import BranchAssignmentModal from './BranchAssignmentModal';
import leadsService, { LeadsService } from '../services/leadsService';
import { useFormFields } from '../hooks/useFormFields';
import FollowUpModal from './FollowUpModal';
import FollowUpHistory from './FollowUpHistory';
import AdvancedLeadStats from './AdvancedLeadStats';
import LeadStatusManager from './LeadStatusManager';
import NotificationToast from './NotificationToast';
import FollowUpManager from './FollowUpManager';
import QuickFollowUp from './QuickFollowUp';
import FollowUpReminder from './FollowUpReminder';
import ReminderNotification from './ReminderNotification';
import FormFieldManager from './FormFieldManager';
import NotificationCenter from './NotificationCenter';
import EmployeeManagementWithTabs from './EmployeeManagementWithTabs';
import FollowUpService from '../services/followUpService';
import notificationService from '../services/notificationService';
import ServiceManager from './ServiceManager';
import LinkGenerator from './LinkGenerator';
import FieldManager from './FieldManager';
import WhatsAppToggle from './WhatsAppToggle';
import WorkflowOverview from './WorkflowOverview';
import { PlusIcon, ChartBarIcon, UsersIcon, XMarkIcon, CogIcon, CheckCircleIcon, ClockIcon, CurrencyDollarIcon, BellIcon, LinkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [filter, setFilter] = useState('all');
  const [leadTypeFilter, setLeadTypeFilter] = useState('tour_booking'); // tour_booking, other_services
  const [searchTerm, setSearchTerm] = useState('');
  const [autoCapture, setAutoCapture] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, followups, employees, performance, workflow
  const [showQuickFollowUp, setShowQuickFollowUp] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState(null);
  const [reminders, setReminders] = useState([]);

  // New follow-up system state
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, lead: null });
  const [selectedLeadForHistory, setSelectedLeadForHistory] = useState(null);
  const [followUpRefresh, setFollowUpRefresh] = useState(0);
  const [upcomingFollowUps, setUpcomingFollowUps] = useState([]);
  const [overdueFollowUps, setOverdueFollowUps] = useState([]);

  // Advanced features state
  const [activeFilter, setActiveFilter] = useState('all');
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [showStatusFlowConfig, setShowStatusFlowConfig] = useState(false);
  const [showFormFieldManager, setShowFormFieldManager] = useState(false);
  const [statusFlow, setStatusFlow] = useState([]);

  // Notification system state
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Bulk assignment state
  const [showBulkAssignment, setShowBulkAssignment] = useState(false);
  const [selectedLeadsForBulkAssignment, setSelectedLeadsForBulkAssignment] = useState([]);

  // Branch assignment state
  const [showBranchAssignment, setShowBranchAssignment] = useState(false);
  const [selectedLeadsForBranchAssignment, setSelectedLeadsForBranchAssignment] = useState([]);



  // Load leads from external API
  useEffect(() => {
    loadLeads();
    loadFollowUpData();
  }, []);

  // Listen for navigate to follow-ups event
  useEffect(() => {
    const handleSwitchToFollowUps = () => {
      setActiveTab('followups');
    };

    window.addEventListener('switchToFollowUpsTab', handleSwitchToFollowUps);
    return () => window.removeEventListener('switchToFollowUpsTab', handleSwitchToFollowUps);
  }, []);

  // Debug log when leads change
  useEffect(() => {
    console.log('Leads updated:', leads);
  }, [leads]);

  // Auto-refresh leads every 2 minutes to keep data synchronized
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing leads data...');
      loadLeads();
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  // Initialize notification system
  useEffect(() => {
    // Load existing notifications
    const notifications = notificationService.getAllNotifications();
    setAllNotifications(notifications);
    setUnreadCount(notificationService.getUnreadCount());

    // Add listener for notification updates
    const handleNotificationUpdate = (updatedNotifications) => {
      setAllNotifications(updatedNotifications);
      setUnreadCount(notificationService.getUnreadCount());
    };

    notificationService.addListener(handleNotificationUpdate);

    // Request browser notification permission
    notificationService.requestNotificationPermission();

    return () => {
      notificationService.removeListener(handleNotificationUpdate);
    };
  }, []);

  // Check for overdue and upcoming follow-ups when leads change
  useEffect(() => {
    if (leads.length > 0) {
      // Check for overdue follow-ups
      notificationService.checkOverdueFollowUps(leads);
      // Check for upcoming follow-ups
      notificationService.checkUpcomingFollowUps(leads);
    }
  }, [leads]);

  const loadLeads = async () => {
    try {
      console.log('🔄 Loading leads...');
      const result = await LeadsService.getAllLeads();

      if (result.success && result.data) {
        console.log('✅ All Leads loaded:', result.data.length, 'leads');

        // Admin dashboard - Show ALL leads without filtering
        // Previously filtered for sales team only, now showing everything
        const allLeads = result.data;

        // Log lead types for debugging
        const tourBookingCount = allLeads.filter(l => l.leadType === 'tour_booking').length;
        const otherServicesCount = allLeads.filter(l => l.leadType === 'other_services').length;
        console.log(`📊 Lead breakdown: Tour Booking: ${tourBookingCount}, Other Services: ${otherServicesCount}`);

        setLeads(allLeads);

        // Clean up stale notifications for leads that no longer exist
        const removedCount = notificationService.cleanupStaleNotifications(allLeads);
        if (removedCount > 0) {
          // Refresh notifications state to reflect cleanup
          const updatedNotifications = notificationService.getAllNotifications();
          setNotifications(updatedNotifications);
        }

        // Check for follow-ups and create notifications
        notificationService.checkOverdueFollowUps(allLeads);
        notificationService.checkUpcomingFollowUps(allLeads);
      } else {
        console.log('⚠️ No leads data received');
        setLeads([]);
      }
    } catch (error) {
      console.error('💥 Error loading leads:', error);
      setLeads([]);
    }
  };

  const handleAddLead = async (leadData) => {
    const result = await LeadsService.createLead(leadData);
    if (result.success) {
      await loadLeads(); // Reload leads from API
      setShowForm(false);
      addNotification('Lead created successfully!', 'success');
    } else {
      addNotification(`Failed to create lead: ${result.error}`, 'error');
    }
  };

  const handleEditLead = async (leadData) => {
    const leadId = editingLead.id || editingLead._id;
    if (!leadId) {
      addNotification('Error: Lead ID not found', 'error');
      return;
    }

    console.log('Updating lead with ID:', leadId);
    console.log('Lead data:', leadData);

    const result = await LeadsService.updateLead(leadId, leadData);
    console.log('Update result:', result);

    if (result.success) {
      // Update the lead in local state immediately with the updated data from backend
      const updatedLead = result.data.lead || result.data;
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          (lead.id === leadId || lead._id === leadId)
            ? { ...lead, ...updatedLead, id: lead.id || lead._id, _id: lead._id || lead.id }
            : lead
        )
      );

      await loadLeads(); // Reload leads from API to ensure consistency
      setEditingLead(null);
      setShowForm(false);
      addNotification('Lead updated successfully!', 'success');
    } else {
      addNotification(`Failed to update lead: ${result.error}`, 'error');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      const result = await LeadsService.deleteLead(leadId);
      if (result.success) {
        await loadLeads(); // Reload leads from API

        // Force refresh notifications to remove deleted lead notifications
        const updatedNotifications = notificationService.getAllNotifications();
        setNotifications(updatedNotifications);

        addNotification('Lead deleted successfully!', 'success');
      } else {
        addNotification(`Failed to delete lead: ${result.error}`, 'error');
      }
    }
  };






  const handleQuotationCreated = () => {
    // Don't close immediately - let the success modal show first
    // The modal will call onClose when user clicks "Close & Return to Dashboard"
    addNotification('Quotation created successfully!', 'success');
    // Refresh leads to update quotation count
    loadLeads();
  };

  const handleStatusChange = async (leadId, newStatus) => {
    const lead = leads.find(l => l._id === leadId);
    if (lead) {
      // Send only the fields that backend expects
      const updatedLead = {
        name: lead.name || lead.customerName,
        email: lead.email,
        phone: lead.phone || lead.contactNumber,
        company: lead.companyName || lead.customerCompany,
        source: lead.source || lead.sourceOfLead,
        status: newStatus,
        priority: lead.priority,
        value: lead.value,
        notes: lead.notes
      };
      const result = await LeadsService.updateLead(leadId, updatedLead);
      if (result.success) {
        await loadLeads(); // Reload leads from API
        addNotification('Lead status updated!', 'success');
      } else {
        addNotification(`Failed to update status: ${result.error}`, 'error');
      }
    }
  };

  // Enhanced filtering logic
  const filteredLeads = (Array.isArray(leads) ? leads : []).filter(lead => {
    if (!lead) return false;

    // Basic filter (status)
    let matchesFilter = true;
    if (activeFilter !== 'all') {
      if (activeFilter.startsWith('source:')) {
        const sourceFilter = activeFilter.replace('source:', '');
        matchesFilter = lead.source === sourceFilter;
      } else {
        matchesFilter = lead.status === activeFilter;
      }
    }

    // Search filter - Search by customer name, email, company, phone
    const matchesSearch = !searchTerm ||
      (lead.customerName || lead.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.customerCompany || lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.contactNumber || lead.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.assignedEmployeeName || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Advanced filters
    let matchesAdvanced = true;
    if (Object.keys(advancedFilters).length > 0) {
      // Status filter
      if (advancedFilters.status && advancedFilters.status.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.status.includes(lead.status);
      }

      // Source filter
      if (advancedFilters.source && advancedFilters.source.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.source.includes(lead.source);
      }

      // Priority filter
      if (advancedFilters.priority && advancedFilters.priority.length > 0) {
        matchesAdvanced = matchesAdvanced && advancedFilters.priority.includes(lead.priority || 'medium');
      }

      // Date range filter
      if (advancedFilters.dateRange && (advancedFilters.dateRange.start || advancedFilters.dateRange.end)) {
        const leadDate = new Date(lead.createdAt);
        if (advancedFilters.dateRange.start) {
          matchesAdvanced = matchesAdvanced && leadDate >= new Date(advancedFilters.dateRange.start);
        }
        if (advancedFilters.dateRange.end) {
          matchesAdvanced = matchesAdvanced && leadDate <= new Date(advancedFilters.dateRange.end);
        }
      }

      // Value range filter
      if (advancedFilters.valueRange && (advancedFilters.valueRange.min || advancedFilters.valueRange.max)) {
        const leadValue = lead.value || 0;
        if (advancedFilters.valueRange.min) {
          matchesAdvanced = matchesAdvanced && leadValue >= parseFloat(advancedFilters.valueRange.min);
        }
        if (advancedFilters.valueRange.max) {
          matchesAdvanced = matchesAdvanced && leadValue <= parseFloat(advancedFilters.valueRange.max);
        }
      }
    }

    // Lead Type filter
    let matchesLeadType = true;
    if (leadTypeFilter !== 'all') {
      matchesLeadType = lead.leadType === leadTypeFilter;
    }

    return matchesFilter && matchesSearch && matchesAdvanced && matchesLeadType;
  });

  const startEdit = (lead) => {
    setEditingLead(lead);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingLead(null);
  };

  const handleGenerateQuotation = (lead) => {
    // Placeholder function for quotation generation
    console.log('Generate quotation for lead:', lead);
    alert('Quotation generation feature coming soon!');
  };

  // Advanced features handlers
  const handleStatsFilter = (filterType) => {
    setActiveFilter(filterType);
    setAdvancedFilters({}); // Clear advanced filters when using stats filter
  };

  const handleAdvancedFilter = (filters) => {
    setAdvancedFilters(filters);
    setActiveFilter('all'); // Reset basic filter when using advanced filters
  };

  const handleStatusFlowSave = (newStatusFlow) => {
    setStatusFlow(newStatusFlow);
    // Here you could save to backend/localStorage
    localStorage.setItem('leadStatusFlow', JSON.stringify(newStatusFlow));
  };

  // Load status flow from localStorage on component mount
  useEffect(() => {
    const savedFlow = localStorage.getItem('leadStatusFlow');
    if (savedFlow) {
      setStatusFlow(JSON.parse(savedFlow));
    }
  }, []);

  // Get unique statuses from statusFlow or use defaults
  const getAvailableStatuses = () => {
    if (statusFlow && statusFlow.length > 0) {
      return statusFlow;
    }
    // Default statuses if no custom flow is set
    return [
      { name: 'new', label: 'New', color: 'blue' },
      { name: 'contacted', label: 'Contacted', color: 'yellow' },
      { name: 'qualified', label: 'Qualified', color: 'green' },
      { name: 'proposal', label: 'Proposal', color: 'purple' },
      { name: 'negotiation', label: 'Negotiation', color: 'orange' },
      { name: 'closed_won', label: 'Closed Won', color: 'emerald' },
      { name: 'closed_lost', label: 'Lost', color: 'red' }
    ];
  };

  const availableStatuses = getAvailableStatuses();

  const handleAutoLead = async (leadData) => {
    // Use the same API call as manual lead creation
    const result = await LeadsService.createLead(leadData);
    if (result.success) {
      await loadLeads(); // Reload leads from API

      // Add notification for auto-captured lead
      const notification = {
        id: Date.now(),
        lead: leadData,
        timestamp: new Date()
      };
      setNotifications(prev => [...prev, notification]);

      // Auto remove notification after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 5000);
    } else {
      console.error('Failed to auto-capture lead:', result.error);
    }
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const addNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, notification]);

    // Auto remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const handleScheduleFollowUp = (lead) => {
    setSelectedLeadForFollowUp(lead);
    setShowQuickFollowUp(true);
  };

  // Notification Center handlers
  const handleNotificationClick = () => {
    setShowNotificationCenter(true);
  };

  const handleCloseNotificationCenter = () => {
    setShowNotificationCenter(false);
  };

  const handleMarkAsRead = (notificationId) => {
    notificationService.markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    notificationService.markAllAsRead();
  };

  const handleDeleteNotification = (notificationId) => {
    notificationService.deleteNotification(notificationId);
  };

  const handleQuickFollowUpSchedule = (followUpData) => {
    // Save to localStorage (same as FollowUpManager)
    const existingFollowUps = JSON.parse(localStorage.getItem('crm-followups') || '[]');
    const updatedFollowUps = [...existingFollowUps, followUpData];
    localStorage.setItem('crm-followups', JSON.stringify(updatedFollowUps));

    // Show notification
    const notification = {
      id: Date.now(),
      lead: selectedLeadForFollowUp,
      timestamp: new Date(),
      type: 'followup'
    };
    setNotifications(prev => [...prev, notification]);

    // Auto remove notification after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 3000);

    setShowQuickFollowUp(false);
    setSelectedLeadForFollowUp(null);
  };

  const handleReminder = (reminderData) => {
    setReminders(prev => [...prev, reminderData]);
  };

  const dismissReminder = (reminderId) => {
    setReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  const markFollowUpComplete = (followUpId) => {
    const followUps = JSON.parse(localStorage.getItem('crm-followups') || '[]');
    const updatedFollowUps = followUps.map(f =>
      f.id === followUpId ? { ...f, status: 'completed' } : f
    );
    localStorage.setItem('crm-followups', JSON.stringify(updatedFollowUps));

    // Remove related reminders
    setReminders(prev => prev.filter(r => r.followUpId !== followUpId));
  };

  const handleAssignmentComplete = async (result) => {
    // Reload leads to reflect assignment changes
    await loadLeads();

    // Show success notification
    if (result.assignedCount) {
      addNotification(`${result.assignedCount} leads assigned to ${result.assignedTo}!`, 'success');
    } else {
      addNotification('Lead assigned successfully!', 'success');
    }
  };

  // New follow-up system handlers
  const handleAddFollowUp = (lead) => {
    setFollowUpModal({ isOpen: true, lead });
  };

  const handleFollowUpAdded = async () => {
    setFollowUpRefresh(prev => prev + 1);
    await loadFollowUpData();
    await loadLeads(); // Refresh leads to get updated follow-up data
    addNotification('Follow-up added successfully!', 'success');
  };

  const handleViewFollowUpHistory = (lead) => {
    setSelectedLeadForHistory(lead);
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, lead: null });
  };

  const closeFollowUpHistory = () => {
    setSelectedLeadForHistory(null);
  };

  // Bulk assignment handlers
  const handleBulkAssignment = (selectedLeads) => {
    if (selectedLeads.length === 0) {
      addNotification('Please select at least one lead for bulk assignment', 'warning');
      return;
    }
    setSelectedLeadsForBulkAssignment(selectedLeads);
    setShowBulkAssignment(true);
  };

  const handleBulkAssignmentComplete = async (result) => {
    if (result.success) {
      addNotification(result.message || 'Leads assigned successfully!', 'success');
      await loadLeads(); // Refresh leads data
      setSelectedLeadsForBulkAssignment([]);
    } else {
      addNotification(result.error || 'Failed to assign leads', 'error');
    }
  };

  const closeBulkAssignment = () => {
    setShowBulkAssignment(false);
    setSelectedLeadsForBulkAssignment([]);
  };

  // Branch assignment handlers
  const handleBranchAssignment = (selectedLeads) => {
    if (selectedLeads.length === 0) {
      addNotification('Please select at least one lead for branch assignment', 'warning');
      return;
    }
    setSelectedLeadsForBranchAssignment(selectedLeads);
    setShowBranchAssignment(true);
  };

  const handleBranchAssignmentComplete = async (result) => {
    if (result.success) {
      addNotification(result.message || 'Leads assigned to branch successfully!', 'success');
      await loadLeads(); // Refresh leads data
      setSelectedLeadsForBranchAssignment([]);
    } else {
      addNotification(result.error || 'Failed to assign leads to branch', 'error');
    }
  };

  const closeBranchAssignment = () => {
    setShowBranchAssignment(false);
    setSelectedLeadsForBranchAssignment([]);
  };

  // Load follow-up data
  const loadFollowUpData = async () => {
    try {
      // Load upcoming follow-ups
      const upcomingResult = await FollowUpService.getUpcomingFollowUps();
      if (upcomingResult.success) {
        setUpcomingFollowUps(upcomingResult.data);
      }

      // Load overdue follow-ups
      const overdueResult = await FollowUpService.getOverdueFollowUps();
      if (overdueResult.success) {
        setOverdueFollowUps(overdueResult.data);
      }

      // Load employee follow-ups data
      const EmployeeDashboardService = require('../services/employeeDashboardService').default;
      const employeeResult = await EmployeeDashboardService.getAssignedLeads();
      if (employeeResult.success && employeeResult.data && Array.isArray(employeeResult.data)) {
        const employeeFollowUps = [];
        employeeResult.data.forEach(lead => {
          if (lead.followUps && lead.followUps.length > 0) {
            lead.followUps.forEach(followUp => {
              const followUpDate = new Date(followUp.nextFollowUpDate || followUp.createdAt);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const followUpData = {
                ...followUp,
                leadId: lead._id,
                leadName: lead.name || lead.customerName || 'Unknown Lead',
                leadCompany: lead.company || lead.customerCompany || '',
                assignedEmployee: lead.assignedEmployeeName || 'Unknown',
                scheduledDate: followUpDate,
                priority: followUp.priority || 'medium',
                type: followUp.type || 'call',
                notes: followUp.summary || followUp.notes || '',
                source: 'employee'
              };

              if (followUpDate < today) {
                setOverdueFollowUps(prev => [...prev, followUpData]);
              } else {
                setUpcomingFollowUps(prev => [...prev, followUpData]);
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('Error loading follow-up data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">📊</span>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  CRM Lead Management
                </h1>
                <p className="text-xs text-gray-500">Professional Sales Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Home Button */}
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${activeTab === 'dashboard'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
              >
                🏠 Home
              </button>

              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${activeTab === 'analytics'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
              >
                📊 Analytics
              </button>

              <button
                onClick={() => setActiveTab('followups')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${activeTab === 'followups'
                  ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white hover:from-pink-700 hover:to-rose-700'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
              >
                📞 Follow-ups
              </button>

              <button
                onClick={() => setActiveTab('employees')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${activeTab === 'employees'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800'
                  }`}
              >
                👥 Employees
              </button>

            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Conditional Content Based on Active Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Quick Stats Overview - Dynamic based on status flow */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
              {/* Total Leads - Always first */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-2.5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-xs font-medium">Total Leads</p>
                    <p className="text-2xl font-bold">{leads.length}</p>
                  </div>
                  <UsersIcon className="h-8 w-8 text-blue-200" />
                </div>
              </div>

              {/* Dynamic status boxes based on availableStatuses */}
              {availableStatuses.slice(0, 4).map((status) => {
                const colorMap = {
                  blue: 'from-blue-500 to-blue-600',
                  green: 'from-green-500 to-green-600',
                  yellow: 'from-yellow-500 to-yellow-600',
                  purple: 'from-purple-500 to-purple-600',
                  red: 'from-red-500 to-red-600',
                  orange: 'from-orange-500 to-orange-600',
                  emerald: 'from-emerald-500 to-emerald-600',
                  amber: 'from-amber-500 to-amber-600',
                  indigo: 'from-indigo-500 to-indigo-600',
                  gray: 'from-gray-500 to-gray-600',
                  slate: 'from-slate-500 to-slate-600'
                };
                const gradientClass = colorMap[status.color] || 'from-gray-500 to-gray-600';
                const count = leads.filter(l => l.status === status.name).length;

                return (
                  <div key={status.name} className={`bg-gradient-to-r ${gradientClass} rounded-lg p-2.5 text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-${status.color}-100 text-xs font-medium`}>{status.label}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                      <CheckCircleIcon className={`h-8 w-8 text-${status.color}-200`} />
                    </div>
                  </div>
                );
              })}
              <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg p-2.5 text-white relative group">
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setActiveTab('followups')}>
                  <div>
                    <p className="text-pink-100 text-xs font-medium">All Follow-ups</p>
                    <p className="text-2xl font-bold">{upcomingFollowUps.length + overdueFollowUps.length}</p>
                  </div>
                  <BellIcon className="h-8 w-8 text-pink-200" />
                </div>
                {/* Dropdown on hover */}
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border-2 border-pink-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="p-2 space-y-1">
                    <div className="flex justify-between items-center px-2 py-1 hover:bg-red-50 rounded text-xs">
                      <span className="text-gray-700 font-medium">Overdue:</span>
                      <span className="text-red-600 font-bold">{overdueFollowUps.length}</span>
                    </div>
                    <div className="flex justify-between items-center px-2 py-1 hover:bg-yellow-50 rounded text-xs">
                      <span className="text-gray-700 font-medium">Today:</span>
                      <span className="text-yellow-600 font-bold">{upcomingFollowUps.filter(f => {
                        const today = new Date().toDateString();
                        return new Date(f.scheduledDate || f.nextFollowUp).toDateString() === today;
                      }).length}</span>
                    </div>
                    <div className="flex justify-between items-center px-2 py-1 hover:bg-green-50 rounded text-xs">
                      <span className="text-gray-700 font-medium">Upcoming:</span>
                      <span className="text-green-600 font-bold">{upcomingFollowUps.filter(f => {
                        const today = new Date().toDateString();
                        return new Date(f.scheduledDate || f.nextFollowUp).toDateString() !== today;
                      }).length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Lead Management Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Header with prominent Add Lead button */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Lead Management</h2>
                    <p className="text-gray-600 text-lg">Manage and track your sales leads efficiently</p>
                  </div>

                  {/* Prominent Add Lead Button */}
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <button
                      onClick={() => setShowForm(true)}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl flex items-center space-x-3 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold text-lg"
                    >
                      <PlusIcon className="h-6 w-6" />
                      <span>Add New Lead</span>
                    </button>

                    {/* Secondary Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={loadLeads}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors border border-gray-300"
                        title="Refresh Leads"
                      >
                        <span>🔄</span>
                      </button>
                      <button
                        onClick={() => setActiveTab('linkgenerator')}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors border border-gray-300"
                        title="Link Generator"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Type Filter Tabs */}
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold text-gray-700 mr-2">Filter by Type:</span>
                  <button
                    onClick={() => setLeadTypeFilter('tour_booking')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${leadTypeFilter === 'tour_booking'
                      ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                  >
                    🏖️ Tour Booking ({leads.filter(l => l.leadType === 'tour_booking').length})
                  </button>
                  <button
                    onClick={() => setLeadTypeFilter('other_services')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${leadTypeFilter === 'other_services'
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                  >
                    📋 Other Services ({leads.filter(l => l.leadType === 'other_services').length})
                  </button>
                </div>
              </div>

              {/* Filters Section */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by customer name, email, phone, or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="w-full lg:w-48">
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Status</option>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed_won">Won</option>
                      <option value="closed_lost">Lost</option>
                    </select>
                  </div>

                  {/* Advanced Filters Toggle */}
                  <div>
                    <AdvancedLeadFilters
                      onFilterChange={handleAdvancedFilter}
                      activeFilters={advancedFilters}
                      leads={leads}
                    />
                  </div>
                </div>
              </div>

              {/* Lead List Section */}
              <div id="leads-table-section" className="p-6">
                <LeadList
                  leads={filteredLeads}
                  onEdit={startEdit}
                  onDelete={handleDeleteLead}
                  onGenerateQuotation={handleGenerateQuotation}
                  onStatusChange={handleStatusChange}
                  onFollowUp={handleAddFollowUp}
                  onViewHistory={handleViewFollowUpHistory}
                  onBulkAssignment={handleBulkAssignment}
                  onBranchAssignment={handleBranchAssignment}
                  leadTypeFilter={leadTypeFilter}
                />
              </div>
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Advanced Analytics Dashboard */}
            <AdvancedLeadStats
              leads={leads}
              onFilterChange={handleStatsFilter}
              activeFilter={activeFilter}
            />

            {/* Filtered Results */}
            {(activeFilter !== 'all' || (activeFilter === 'all' && filteredLeads.length > 0)) && (
              <div className="mt-6">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                  <div className="border-b border-gray-200 p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {activeFilter === 'all' ? 'All Leads' : `Filtered Results: ${activeFilter.replace('source:', '').replace('_', ' ').toUpperCase()}`}
                    </h3>
                    <p className="text-gray-600 text-sm">Showing {filteredLeads.length} leads</p>
                  </div>
                  <div id="analytics-leads-table-section" className="p-4">
                    <LeadList
                      leads={filteredLeads}
                      onEdit={startEdit}
                      onDelete={handleDeleteLead}
                      onGenerateQuotation={handleGenerateQuotation}
                      onStatusChange={handleStatusChange}
                      onFollowUp={handleAddFollowUp}
                      onViewHistory={handleViewFollowUpHistory}
                      onBulkAssignment={handleBulkAssignment}
                      onBranchAssignment={handleBranchAssignment}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Follow-Up Manager Tab */}
        {activeTab === 'followups' && (
          <FollowUpManager
            leads={leads}
            onUpdateLead={(leadData) => {
              setLeads(leads.map(lead =>
                lead.id === leadData.id ? leadData : lead
              ));
            }}
          />
        )}

        {/* Link Generator Tab */}
        {activeTab === 'linkgenerator' && (
          <LinkGenerator />
        )}

        {/* Employee Management Tab */}
        {activeTab === 'employees' && (
          <EmployeeManagementWithTabs />
        )}


      </div>

      {/* Lead Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <LeadForm
              lead={editingLead}
              onSubmit={editingLead ? handleEditLead : handleAddLead}
              onCancel={cancelForm}
            />
          </div>
        </div>
      )}

      {/* Quick Follow-Up Modal */}
      {showQuickFollowUp && selectedLeadForFollowUp && (
        <QuickFollowUp
          lead={selectedLeadForFollowUp}
          onSchedule={handleQuickFollowUpSchedule}
          onClose={() => {
            setShowQuickFollowUp(false);
            setSelectedLeadForFollowUp(null);
          }}
        />
      )}



      {/* Notification Toasts */}
      <NotificationToast
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Follow-Up Reminder System */}
      <FollowUpReminder onReminder={handleReminder} />

      {/* Reminder Notifications */}
      <ReminderNotification
        reminders={reminders}
        onDismiss={dismissReminder}
        onMarkComplete={markFollowUpComplete}
      />

      {/* New Follow-up Modal */}
      <FollowUpModal
        isOpen={followUpModal.isOpen}
        onClose={closeFollowUpModal}
        lead={followUpModal.lead}
        onFollowUpAdded={handleFollowUpAdded}
      />

      {/* Follow-up History Modal */}
      {selectedLeadForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Follow-up History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Lead: {selectedLeadForHistory.name} • {selectedLeadForHistory.company || 'No Company'}
                </p>
              </div>
              <button
                onClick={closeFollowUpHistory}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <FollowUpHistory
                leadId={selectedLeadForHistory._id}
                refreshTrigger={followUpRefresh}
              />
            </div>
          </div>
        </div>
      )}

      {/* Status Management Modal */}
      {showStatusFlowConfig && (
        <LeadStatusManager
          isOpen={showStatusFlowConfig}
          onStatusesUpdated={handleStatusFlowSave}
          onClose={() => setShowStatusFlowConfig(false)}
        />
      )}

      {/* Form Field Manager Modal */}
      {showFormFieldManager && (
        <FormFieldManager
          isOpen={showFormFieldManager}
          onClose={() => setShowFormFieldManager(false)}
        />
      )}

      {/* Notification Center Modal */}
      {showNotificationCenter && (
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={handleCloseNotificationCenter}
          notifications={allNotifications}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDeleteNotification={handleDeleteNotification}
        />
      )}

      {/* Bulk Lead Assignment Modal */}
      {showBulkAssignment && (
        <BulkLeadAssignmentModal
          isOpen={showBulkAssignment}
          onClose={closeBulkAssignment}
          selectedLeads={selectedLeadsForBulkAssignment}
          onAssignmentComplete={handleBulkAssignmentComplete}
        />
      )}

      {/* Branch Assignment Modal */}
      {showBranchAssignment && (
        <BranchAssignmentModal
          isOpen={showBranchAssignment}
          onClose={closeBranchAssignment}
          selectedLeads={selectedLeadsForBranchAssignment}
          onAssignmentComplete={handleBranchAssignmentComplete}
        />
      )}


    </div>
  );
};

export default LeadManagement;
